import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { auth } from '../config/firebase';
import adminApiService from '../services/adminApiService';
import toast from 'react-hot-toast';

interface AdminPermission {
  resource: string;
  actions: string[];
}

interface AdminUser {
  uid: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'moderator' | 'analyst';
  permissions: AdminPermission[];
  isActive: boolean;
  lastLogin: any;
  createdAt: any;
}

interface AdminContextType {
  adminUser: AdminUser | null;
  isAdminAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (resource: string, action: string) => boolean;
  refreshAdminData: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user has specific permission
  const hasPermission = (resource: string, action: string): boolean => {
    if (!adminUser) return false;
    
    // Super admin has all permissions
    if (adminUser.role === 'super_admin') return true;
    
    // Check specific permissions
    return adminUser.permissions.some(permission => {
      if (permission.resource === resource || permission.resource === '*') {
        return permission.actions.includes(action) || permission.actions.includes('*');
      }
      return false;
    });
  };

  // Refresh admin data from server
  const refreshAdminData = async (): Promise<void> => {
    try {
      if (localStorage.getItem('authToken')) {
        const adminData = await adminApiService.getAdminProfile();
        setAdminUser(adminData);
        setIsAdminAuthenticated(true);
      }
    } catch (error: any) {
      console.error('Failed to refresh admin data:', error);
      setAdminUser(null);
      setIsAdminAuthenticated(false);
    }
  };

  // Admin login
  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);

      // Authenticate against the native auth endpoint (stores the JWT).
      const token = await adminApiService.authenticate(email, password);

      // Then validate admin access.
      const result = await adminApiService.validateAdminAccess(token);

      if (result.success) {
        setAdminUser(result.data);
        setIsAdminAuthenticated(true);
        await refreshAdminData();
      } else {
        // Not an admin — drop the session.
        await auth.signOut();
        throw new Error(result.message || 'Admin access denied');
      }
    } catch (error: any) {
      console.error('Admin login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Admin logout
  const logout = async (): Promise<void> => {
    try {
      await auth.signOut();
      setAdminUser(null);
      setIsAdminAuthenticated(false);
      toast.success('Logged out successfully');
    } catch (error: any) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  // Restore admin session on mount from any stored JWT (admin routes only).
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const hasToken = Boolean(localStorage.getItem('authToken'));
      const isAdminRoute = window.location.pathname.startsWith('/admin');

      if (hasToken && isAdminRoute) {
        try {
          const adminData = await adminApiService.getAdminProfile();
          if (adminData && adminData.isActive) {
            setAdminUser(adminData);
            setIsAdminAuthenticated(true);
          } else {
            setAdminUser(null);
            setIsAdminAuthenticated(false);
          }
        } catch (error) {
          // Logged-in user is not an admin.
          setAdminUser(null);
          setIsAdminAuthenticated(false);
        }
      } else {
        setAdminUser(null);
        setIsAdminAuthenticated(false);
      }

      setIsLoading(false);
    })();
  }, []);

  const value: AdminContextType = {
    adminUser,
    isAdminAuthenticated,
    isLoading,
    login,
    logout,
    hasPermission,
    refreshAdminData
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext; 